import { useEffect, type RefObject } from 'react';
import { cycleTabFocus } from '../utils/focus-trap';

interface UseDialogFocusOptions {
  isOpen: boolean;
  /** The dialog element. Needs `tabIndex={-1}` so it can take focus itself. */
  dialogRef: RefObject<HTMLElement | null>;
  /** Called on Escape. Omit when the dialog must not be dismissible that way. */
  onClose?: () => void;
  /**
   * Set false when the component already has its own Escape handling, so the
   * dialog does not get closed twice by one keypress.
   */
  handleEscape?: boolean;
  /** Prevent the page behind from scrolling while open. */
  lockScroll?: boolean;
}

/**
 * Focus behaviour every `aria-modal` dialog needs (WCAG 2.1.2, 2.4.3).
 *
 * `aria-modal="true"` tells assistive technology the rest of the page is inert,
 * but nothing enforces that. Without this, Tab walks straight out of the dialog
 * into content a screen reader has just been told does not exist, and closing
 * the dialog drops focus back to the top of the document.
 *
 * Extracted from `ConfirmModal`, which worked this out first. Three details in
 * here are load-bearing and easy to lose if you reimplement it per dialog:
 *
 * - Escape is handled in the **capture** phase and stops propagation, so a
 *   confirm nested inside a panel closes only itself rather than both.
 * - Focus is restored only if the previously focused element is still
 *   `isConnected`; the element that opened the dialog is often unmounted by the
 *   time it closes.
 * - The dialog container takes focus only when nothing inside it has it already,
 *   so an `autoFocus` field keeps the caret it just claimed.
 * - Scroll lock restores the previous `overflow` value rather than clearing it,
 *   so a dialog opened from an already-locked panel does not unlock the page
 *   behind it on close.
 */
export const useDialogFocus = ({
  isOpen,
  dialogRef,
  onClose,
  handleEscape = true,
  lockScroll = false,
}: UseDialogFocusOptions) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (e.key === 'Escape' && handleEscape && onClose) {
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;
      cycleTabFocus(dialog, e);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose, handleEscape, dialogRef]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement;

    /*
      Only take focus if it is not already inside the dialog. A dialog with an autoFocus field
      has already put the caret where the user wants to type by the time this runs (React
      applies autoFocus during commit, before effects), and moving it to the container leaves
      them typing into nothing — which reads as a form that swallowed their keystrokes rather
      than as a focus nicety. Focus is inside the dialog either way, which is what WCAG 2.4.3
      is asking for.
    */
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) {
      dialog.focus();
    }

    return () => {
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, dialogRef]);

  useEffect(() => {
    if (!isOpen || !lockScroll) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, lockScroll]);
};
